import { DataGrid } from 'devextreme-react/data-grid';
import { useCallback } from 'react';
import { getSelectedDataByKey } from '../services/utils';
import { formatMessage } from 'devextreme/localization';
import { trigger } from 'devextreme/events';

export const useSelectionChanged = <TData>(
  rowKey: '' | keyof TData,
  innerGridRef: React.MutableRefObject<DataGrid<TData, keyof TData> | undefined>,
  onSelectedRowsChanged?: (data: TData[]) => void,
  onFocusedRowChanged?: (data: TData | null, rows: TData[] | null) => void,
) => {
  const focusToSelectedItem = useCallback((ev) => {
    const key = ev.selectedRowKeys[0];
    if (key !== undefined) {
      ev.component.option('focusedRowKey', key);
      const rowIndex = ev.component.getRowIndexByKey(key);
      ev.component.navigateToRow(rowIndex);
    }
  }, []);

  const changeSelectionInfo = useCallback((ev) => {
    const selectedCount = ev.selectedRowKeys.length;

    innerGridRef.current?.instance.option(
      'pager.infoText',
      `${formatMessage('dxTagBox-selected', String(selectedCount))}
       \u00A0\u00A0\u00A0\u00A0
       ${formatMessage('dxPager-infoText')}`,
    );
  }, []);

  const selectionChanged = useCallback(
    (ev) => {
      changeSelectionInfo(ev);
      focusToSelectedItem(ev);

      if (onSelectedRowsChanged || onFocusedRowChanged) {
        const selectedRowsData = getSelectedDataByKey(innerGridRef?.current, rowKey);
        onSelectedRowsChanged?.(selectedRowsData);

        const lastSelectedElement =
          selectedRowsData.find((data) => data[rowKey as keyof typeof data] === ev.currentSelectedRowKeys?.[0]) ??
          selectedRowsData?.[selectedRowsData.length - 1] ??
          null;
        onFocusedRowChanged?.(lastSelectedElement, selectedRowsData);
      } // if

      // Select first row if selected rows empty
      if (ev.selectedRowsData?.length === 0) {
        const rows = ev.element?.querySelectorAll('.dx-data-row');
        setTimeout(() => trigger(rows[0], 'dxclick'));
      } // if
    },
    [onSelectedRowsChanged, onFocusedRowChanged],
  );

  const onRowClick = (e: any) => {
    const isModifierKey = e.event.ctrlKey || e.event.metaKey || e.event.shiftKey;

    if (isModifierKey) return;

    innerGridRef?.current?.instance.selectRows([e.data?.[rowKey]], false);
  };

  return {
    selectionChanged,
    onRowClick,
  };
};
